import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Button,
  Space,
  List,
  Typography,
  Card,
  InputNumber,
  Input,
  Empty,
  Tag,
  Divider,
  Popconfirm,
  Form,
} from 'antd';
import {
  BulbOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { SourceChapterSuggestion } from '../../types';
import { sourceStoriesApi } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';
import AutoChapterConfigModal from '../../components/admin/tasks/AutoChapterConfigModal';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ChapterSuggestionsDrawerProps {
  visible: boolean;
  storyId: string;
  storyName: string;
  onClose: () => void;
}

const ChapterSuggestionsDrawer: React.FC<ChapterSuggestionsDrawerProps> = ({
  visible,
  storyId,
  storyName,
  onClose,
}) => {
  const [generateCount, setGenerateCount] = useState<number>(5);
  const [direction, setDirection] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  
  const [autoChapterVisible, setAutoChapterVisible] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<SourceChapterSuggestion | null>(null);

  const { loading: generating, execute: executeGenerate } = useAsyncOperation();
  const { loading: updating, execute: executeUpdate } = useAsyncOperation();
  const { loading: deleting, execute: executeDelete } = useAsyncOperation();

  // Fetch suggestions
  const {
    data: suggestionsData,
    loading: suggestionsLoading,
    refresh: refreshSuggestions,
  } = useApi(
    useCallback(
      () => (visible ? sourceStoriesApi.getChapterSuggestions(storyId) : Promise.resolve({ data: [] })),
      [storyId, visible]
    ),
    { immediate: visible }
  );

  const handleGenerate = async () => {
    const result = await executeGenerate(
      () => sourceStoriesApi.generateChapterSuggestions(storyId, { count: generateCount, direction }),
      { 
        successMessage: 'Đã tạo gợi ý mới thành công!',
        showSuccess: true 
      }
    );
    if (result) {
      refreshSuggestions();
    }
  };

  const handleEdit = (item: SourceChapterSuggestion) => {
    setEditingId(item.id);
    form.setFieldsValue({
      title: item.title,
      instruction: item.instruction,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    form.resetFields();
  };

  const handleSaveEdit = async (suggestionId: string) => {
    const values = await form.validateFields();
    const result = await executeUpdate(
      () => sourceStoriesApi.updateChapterSuggestion(storyId, suggestionId, values),
      {
        successMessage: 'Cập nhật gợi ý thành công!',
        showSuccess: true,
      }
    );

    if (result) {
      setEditingId(null);
      refreshSuggestions();
    }
  };

  const handleDelete = async (suggestionId: string) => {
    const result = await executeDelete(
      () => sourceStoriesApi.deleteChapterSuggestion(storyId, suggestionId),
      {
        successMessage: 'Đã xóa gợi ý!',
        showSuccess: true,
      }
    );

    if (result) {
      refreshSuggestions();
    }
  };

  const handleCreateChapterFromSuggestion = (suggestion: SourceChapterSuggestion) => {
    setSelectedSuggestion(suggestion);
    setAutoChapterVisible(true);
  };

  const handleAutoChapterSuccess = async () => {
    if (selectedSuggestion) {
      await sourceStoriesApi.updateChapterSuggestion(storyId, selectedSuggestion.id, { status: 'USED' });
      refreshSuggestions();
    }
    setAutoChapterVisible(false);
    setSelectedSuggestion(null);
  };

  const suggestions = suggestionsData?.data || [];

  return (
    <Drawer
      title={
        <Space>
          <BulbOutlined style={{ color: '#faad14' }} />
          <span>Gợi ý chủ đề Chapter: {storyName}</span>
        </Space>
      }
      placement="right"
      width={window.innerWidth < 600 ? '100%' : 600}
      onClose={onClose}
      open={visible}
    >
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>Tạo gợi ý mới</Title>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>Số lượng:</span>
            <InputNumber 
              min={1} 
              max={20} 
              value={generateCount} 
              onChange={(val) => setGenerateCount(val || 5)} 
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>Định hướng mong muốn (tùy chọn):</div>
            <TextArea 
              rows={3} 
              placeholder="Ví dụ: Tập trung vào quá khứ của nhân vật chính, hoặc thêm một biến cố bất ngờ..."
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
            />
          </div>
          <Button 
            type="primary" 
            icon={<BulbOutlined />} 
            onClick={handleGenerate}
            loading={generating}
            block
          >
            AI Gợi ý chủ đề
          </Button>
        </Space>
      </div>

      <Divider>Danh sách gợi ý</Divider>

      <List
        loading={suggestionsLoading}
        dataSource={suggestions}
        locale={{
          emptyText: <Empty description="Chưa có gợi ý nào. Hãy bấm nút 'AI Gợi ý chủ đề' để bắt đầu." />
        }}
        renderItem={(item: SourceChapterSuggestion) => {
          const isEditing = editingId === item.id;

          return (
            <List.Item key={item.id} style={{ padding: '12px 0' }}>
              {isEditing ? (
                <Card size="small" style={{ width: '100%', borderRadius: 8, border: '1px solid #1890ff' }}>
                  <Form form={form} layout="vertical">
                    <Form.Item
                      name="title"
                      label="Tên chủ đề"
                      rules={[{ required: true, message: 'Vui lòng nhập tên chủ đề' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="instruction"
                      label="Hướng dẫn chi tiết"
                      rules={[{ required: true, message: 'Vui lòng nhập hướng dẫn' }]}
                    >
                      <TextArea rows={4} />
                    </Form.Item>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                      <Button size="small" icon={<CloseOutlined />} onClick={handleCancelEdit}>
                        Hủy
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={updating}
                        onClick={() => handleSaveEdit(item.id)}
                      >
                        Lưu
                      </Button>
                    </Space>
                  </Form>
                </Card>
              ) : (
                <Card 
                  size="small" 
                  style={{ width: '100%', borderRadius: 8 }}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <Text strong style={{ color: item.status === 'USED' ? '#8c8c8c' : 'inherit' }}>
                        {item.title}
                      </Text>
                      <Tag color={item.status === 'USED' ? 'success' : 'processing'} style={{ margin: 0 }}>
                        {item.status === 'USED' ? 'Đã dùng' : 'Chờ dùng'}
                      </Tag>
                    </div>
                  }
                >
                  <Paragraph 
                    ellipsis={{ rows: 3, expandable: true, symbol: 'Xem thêm' }}
                    style={{ marginBottom: 8, fontSize: 14 }}
                  >
                    {item.instruction}
                  </Paragraph>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 12
                  }}>
                    <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      Tạo ngày: {formatDate(item.createdAt)}
                    </Text>
                    {item.direction && (
                      <Tag style={{ fontSize: 11, margin: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Định hướng: {item.direction}
                      </Tag>
                    )}
                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                    <Button 
                      size="small"
                      icon={<RocketOutlined />} 
                      onClick={() => handleCreateChapterFromSuggestion(item)}
                      disabled={editingId !== null || item.status === 'USED'}
                    >
                      Tạo chapter
                    </Button>
                    <Button 
                      size="small"
                      icon={<EditOutlined />} 
                      onClick={() => handleEdit(item)}
                      disabled={editingId !== null || item.status === 'USED'}
                    >
                      Sửa
                    </Button>
                    <Popconfirm
                      title="Xóa gợi ý này?"
                      onConfirm={() => handleDelete(item.id)}
                      okText="Xóa"
                      cancelText="Hủy"
                      okButtonProps={{ danger: true, loading: deleting }}
                      disabled={editingId !== null}
                    >
                      <Button 
                        size="small"
                        danger 
                        icon={<DeleteOutlined />}
                        disabled={editingId !== null}
                      >
                        Xóa
                      </Button>
                    </Popconfirm>
                  </div>
                </Card>
              )}
            </List.Item>
          );
        }}
      />

      <AutoChapterConfigModal
        visible={autoChapterVisible}
        storyId={storyId}
        storyName={storyName}
        initialValues={{
          chapterName: selectedSuggestion?.title,
          instruction: selectedSuggestion?.instruction,
        }}
        onCancel={() => {
          setAutoChapterVisible(false);
          setSelectedSuggestion(null);
        }}
        onSuccess={handleAutoChapterSuccess}
      />
    </Drawer>
  );
};

export default ChapterSuggestionsDrawer;
